import { MEMBER_UPDATE_EVENT } from "../../lib/constants.js";
import { isIconSlot } from "../../lib/icons.js";
import { controlMap } from "../../lib/control.js";
import _sfc_main$1 from "./option_bar_control.vue.js";
import { convert } from "../../lib/convert.js";
import { buildDependencyMap, shouldHideProp } from "../../lib/prop_dependencies.js";
import { getDisabledValues, shouldExclude } from "../../lib/exclusion_rules.js";
import { Fragment, computed, createCommentVNode, createElementBlock, createVNode, openBlock, reactive, renderList, unref } from "vue";
import "@dialpad/dialtone-vue";
//#region src/components/option_bar/option_bar_member_group.vue
var _hoisted_1 = {
	key: 0,
	"data-qa": "dtc-option-bar-member-group-control"
};
var _sfc_main = /* @__PURE__ */ Object.assign({ name: "DtcOptionBarMemberGroup" }, {
	props: {
		component: {
			type: Object,
			required: true
		},
		members: {
			type: Array,
			required: true
		},
		values: {
			type: Object,
			required: true
		},
		controlSelector: {
			type: Function,
			required: true
		},
		exclusionRules: {
			type: Array,
			default: () => []
		},
		propValues: {
			type: Object,
			default: () => ({})
		},
		memberGroup: {
			type: String,
			default: "props"
		}
	},
	emits: [MEMBER_UPDATE_EVENT],
	setup(__props, { emit: __emit }) {
		const ICON_SLOT_ORDER = [
			"startIcon",
			"endIcon",
			"blockStartIcon",
			"blockEndIcon",
			"icon"
		];
		const PROP_PRIORITY = [
			"title",
			"as",
			"label",
			"importance",
			"kind",
			"size",
			"placement",
			"tone",
			"align",
			"density",
			"strength",
			"type",
			"underline",
			"selected",
			"active",
			"disabled",
			"showDivider",
			"color",
			"description",
			"scrollbar",
			"surface",
			"borderColor",
			"borderRadius",
			"borderWidth",
			"borderWidthBlock",
			"borderWidthBlockEnd",
			"borderWidthBlockStart",
			"borderWidthInline",
			"borderWidthInlineEnd",
			"borderWidthInlineStart",
			"padding",
			"paddingBlock",
			"paddingBlockEnd",
			"paddingBlockStart",
			"paddingInline",
			"paddingInlineEnd",
			"paddingInlineStart",
			"blockSize",
			"inlineSize",
			"maxBlockSize",
			"minBlockSize",
			"maxInlineSize",
			"minInlineSize",
			"shadow",
			"overflow"
		];
		const SLOT_PRIORITY = [
			"start",
			"end",
			"inlineStart",
			"inlineEnd",
			"blockStart",
			"blockEnd",
			"leading",
			"trailing"
		];
		function getPropTier(member) {
			const priorityIdx = PROP_PRIORITY.indexOf(member.name);
			if (priorityIdx !== -1) return [0, priorityIdx];
			if (member.name?.startsWith("aria")) return [1, 0];
			if (member.types?.includes("boolean")) return [2, 0];
			if (member.name?.endsWith("Class")) return [4, 0];
			return [3, 0];
		}
		function getSlotTier(member) {
			if (member.name === "default") return [0, 0];
			if (isIconSlot(member)) return [1, ICON_SLOT_ORDER.indexOf(member.name)];
			const priorityIdx = SLOT_PRIORITY.indexOf(member.name);
			if (priorityIdx !== -1) return [2, priorityIdx];
			return [3, 0];
		}
		const props = __props;
		const emit = __emit;
		const dependencyMap = computed(() => buildDependencyMap(props.members));
		/**
		* The member map is a reactive data object that wraps each member and
		* provides additional data that the 'option bar control' component
		* needs without affecting the original member data object.
		*
		* @type {object}
		*/
		const memberMap = computed(() => {
			if (!props.members?.length) return reactive({});
			const depMap = dependencyMap.value;
			const childSet = new Set(depMap.keys());
			const getTier = props.memberGroup === "slots" ? getSlotTier : getPropTier;
			const sortFn = (a, b) => {
				const [aTier, aIdx] = getTier(a);
				const [bTier, bIdx] = getTier(b);
				if (aTier !== bTier) return aTier - bTier;
				if (aIdx !== bIdx) return aIdx - bIdx;
				return (a.name ?? "").localeCompare(b.name ?? "");
			};
			const parents = [...props.members].filter((m) => !childSet.has(m.name)).sort(sortFn);
			const childrenByParent = /* @__PURE__ */ new Map();
			for (const [child, parent] of depMap) {
				if (!childrenByParent.has(parent)) childrenByParent.set(parent, []);
				childrenByParent.get(parent).push(props.members.find((m) => m.name === child));
			}
			childrenByParent.forEach((arr) => arr.sort(sortFn));
			const sorted = parents.flatMap((m) => [m, ...childrenByParent.get(m.name) || []]);
			return reactive({ ...Object.fromEntries(sorted.map((member) => {
				return [getMemberKey(member), extendMember(member)];
			})) });
		});
		/**
		* Used to match members in the 'member map' to their respective values.
		*
		* @param member
		* @returns {*}
		*/
		function getMemberKey(member) {
			return member.name;
		}
		/**
		* Attempts to get the control data for a given control.
		* If the control does not exist, gets the 'base' control data.
		*
		* @param member
		* @returns {object} The control data from the 'control map'.
		*/
		function getControlData(member) {
			return controlMap[member.control] ?? controlMap.base;
		}
		function isVModel(member) {
			const tags = member.tags;
			return tags ? "model" in tags : false;
		}
		/**
		* Wraps a member with an object containing additional data about the member.
		* This is used by the 'member map' to hold data about controls.
		*
		* @param member
		* @returns {object}
		*/
		function extendMember(member) {
			const key = getMemberKey(member);
			const value = props.values[key];
			const [validControls, control] = props.controlSelector(member, value);
			const dynamicHide = !member.required && shouldHideProp(key, dependencyMap.value, props.values);
			const isDeprecated = !!member.tags?.deprecated || member.description?.startsWith("@deprecated");
			const isExcluded = !member.required && shouldExclude(key, props.memberGroup, props.exclusionRules, props.propValues);
			return {
				...member,
				control,
				validControls,
				hideControl: member.hideControl || isDeprecated,
				disableControl: dynamicHide || isExcluded
			};
		}
		/**
		* Emits an update to a member.
		*
		* @param key - The member key
		* @param e - The updated value
		*/
		function updateMember(e, key) {
			emit(MEMBER_UPDATE_EVENT, {
				member: key,
				value: e
			});
		}
		/**
		* Updates the member's control in the 'member map'.
		*
		* @param e - The updated control
		* @param key - The member key
		*/
		function updateControl(e, key) {
			const member = memberMap.value[key];
			let value;
			try {
				value = convert(member.control, e, props.values[key]);
			} catch {
				console.warn(`${member.name}: Unable to convert ${member.control} to ${e}`);
			}
			member.control = e;
			updateMember(value ?? getControlData(member).default(member), key);
		}
		return (_ctx, _cache) => {
			return openBlock(true), createElementBlock(Fragment, null, renderList(memberMap.value, (member, key) => {
				return openBlock(), createElementBlock(Fragment, { key }, [!member.hideControl ? (openBlock(), createElementBlock("div", _hoisted_1, [createVNode(_sfc_main$1, {
					value: __props.values[key],
					label: member.label,
					"control-data": getControlData(member),
					"valid-controls": member.validControls,
					description: member.description,
					"v-model": isVModel(member),
					required: member.required,
					locked: member.lockControl,
					disabled: member.disableControl,
					args: {
						defaultValue: member.defaultValue ?? member.initialValue,
						validValues: member.values,
						validTypes: member.types,
						tags: member.tags,
						bindings: member.bindings,
						tokenCategory: member.tokenCategory,
						propValues: __props.propValues,
						disabledValues: unref(getDisabledValues)(key, props.exclusionRules, props.propValues)
					},
					"onUpdate:value": (e) => updateMember(e, key),
					"onUpdate:control": (e) => updateControl(e, key)
				}, null, 8, [
					"value",
					"label",
					"control-data",
					"valid-controls",
					"description",
					"v-model",
					"required",
					"locked",
					"disabled",
					"args",
					"onUpdate:value",
					"onUpdate:control"
				])])) : createCommentVNode("", true)], 64);
			}), 128);
		};
	}
});
//#endregion
export { _sfc_main as default };

//# sourceMappingURL=option_bar_member_group.vue.js.map